import { FieldValues, RegisterOptions } from 'react-hook-form';

export enum FieldStatus {
    INIT = 'INIT',
    RESET = 'RESET',
    HAS_VALUE = 'HAS_VALUE',
    INITIAL_VALUE = 'INITIAL_VALUE',
    INVALID = 'INVALID',
    DEFAULT_DATA = 'DEFAULT_DATA',
}

export enum ConfigFieldType {
    DATA = 'DATA',
    OPTIONS = 'OPTIONS',
}

export type RulesRHF = Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;

// FieldData definition
export interface Serializable {
    serialize(): string;
}

export interface FieldData<TValue extends Value, KInitalValue extends InitialValue> extends Serializable {
    status: FieldStatus;
    value: TValue;
    initialValue: string;
    hasValue(): boolean;
    getInitialValue(): KInitalValue;
    clear?(): FieldData<TValue, KInitalValue>;
    reset?(): FieldData<TValue, KInitalValue>;
    type: ConfigFieldType;
}

// Available FieldData
export type SelectorFieldDataImpl = FieldData<SelectorValueOptions, SelectorInitialValue>;
export type TextInputFieldData = FieldData<string, string>;
export type RangeFieldData = FieldData<GaugeRangeValue, GaugeRangeValue>;
export type ChartLimitFieldData = FieldData<ChartLimitValue, ChartLimitValue>;
export type FormFieldData = FieldData<Value, InitialValue>;

export type Fields<TValue = Serializable> = {
    [key: string]: TValue;
};
export type FormFields = Fields<FormFieldData>;

// Values for FieldData
export enum SelectorType {
    SELECT = 'SELECT',
    FIELD_GROUP = 'FIELD_GROUP',
    PERIOD = 'PERIOD',
    TREE = 'TREE',
}
export enum PeriodSelectorPeriods {
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
    QUARTER = 'QUARTER',
    SEMESTER = 'SEMESTER',
    YEAR = 'YEAR',
}
export type PeriodFormState = {
    isToEnd: boolean;
    isWithCurrent: boolean;
    period: PeriodSelectorPeriods;
    number: number;
};
export type SelectorOption = {
    type: SelectorType;
    value: string;
    label: string;
    key?: string;
    title?: string;
    color?: string;
    icon?: string;
    logoUrl?: string;
    id?: string;
    tooltip?: string;
    isDisabled?: boolean;
    disabled?: boolean;
    disableCheckbox?: boolean;
    checkable?: boolean;
    selectable?: boolean;
    selected?: boolean;
    state?: PeriodFormState;
};
export type TreeSelectorOption = SelectorOption & {
    key: string;
    title: string;
    children?: TreeSelectorOption[];
};
export type SelectorValueOptions = SelectorOption[];
export type SelectorInitialValue = string[];

export type GaugeRangeValue = {
    from: TextInputFieldData;
    to: TextInputFieldData;
    name: TextInputFieldData;
    color: TextInputFieldData;
};

export type ChartLimitValue = {
    name: TextInputFieldData;
    color: TextInputFieldData;
    value: TextInputFieldData;
};

export type Value = SelectorValueOptions | GaugeRangeValue | ChartLimitValue | string;
export type InitialValue = SelectorInitialValue | GaugeRangeValue | ChartLimitValue | string;

export type WidgetValues = { key: string; value: string };
